import {
  HHAccordion,
  HHAccordionDetails,
  HHAccordionSummary,
  HHFormControlLabel,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { ExpandMore } from '@mui/icons-material';
import { Checkbox } from '@mui/material';
import { useMemo } from 'react';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import {
  ProductOfferingsQuery,
  ScopeableType,
  useGetAllProductsQuery,
  useProductOfferingsQuery,
} from '../../../types';
import {
  PRODUCT_OFFERING_MULTI_SELECT_TEST_ID,
  ScopeItem,
} from './scope-selector-constants';

type ProductOfferingItem = ProductOfferingsQuery['productOfferings'][number];

export interface ProductOfferingMultiSelectProps {
  disabled?: boolean;
  error?: FieldError;
}

interface OfferingGroup {
  productId: number | null;
  productName: string;
  offerings: ProductOfferingItem[];
}

const UNGROUPED = 'Other';

export const ProductOfferingMultiSelect = ({
  disabled,
  error,
}: ProductOfferingMultiSelectProps): JSX.Element => {
  const { control } = useFormContext();
  const { data, loading, error: queryError } = useProductOfferingsQuery();
  const { data: productsData } = useGetAllProductsQuery();

  const productNameById = useMemo(() => {
    const map = new Map<number, string>();
    productsData?.getAllProducts?.forEach(p => map.set(p.id, p.name));
    return map;
  }, [productsData]);

  const groups: OfferingGroup[] = useMemo(() => {
    const offerings = data?.productOfferings ?? [];
    const byProduct = new Map<number, ProductOfferingItem[]>();
    const ungrouped: ProductOfferingItem[] = [];

    offerings.forEach(o => {
      if (productNameById.has(o.productId)) {
        const existing = byProduct.get(o.productId) ?? [];
        existing.push(o);
        byProduct.set(o.productId, existing);
      } else {
        ungrouped.push(o);
      }
    });

    const sortedGroups: OfferingGroup[] = Array.from(byProduct.entries())
      .map(([productId, items]) => ({
        productId,
        productName: productNameById.get(productId) ?? UNGROUPED,
        offerings: [...items].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.productName.localeCompare(b.productName));

    if (ungrouped.length > 0) {
      sortedGroups.push({
        productId: null,
        productName: UNGROUPED,
        offerings: [...ungrouped].sort((a, b) => a.name.localeCompare(b.name)),
      });
    }

    return sortedGroups;
  }, [data, productNameById]);

  const errorMessage =
    error?.message ??
    (queryError ? 'Failed to load product offerings' : undefined);

  return (
    <HHStack data-testid={PRODUCT_OFFERING_MULTI_SELECT_TEST_ID} spacing={1}>
      <HHTypography hhVariant="body2" color="text.secondary">
        Product offerings
      </HHTypography>
      {errorMessage && (
        <HHTypography
          hhVariant="variant-bypass"
          variant="caption"
          color="error"
        >
          {errorMessage}
        </HHTypography>
      )}
      {loading && (
        <HHTypography hhVariant="body2">Loading offerings...</HHTypography>
      )}
      <Controller
        control={control}
        name="scopes"
        render={({ field: { onChange, value } }): JSX.Element => {
          const selectedScopes: ScopeItem[] = (value as ScopeItem[]) ?? [];
          const selectedIds = new Set(
            selectedScopes
              .filter(
                s =>
                  s.scopeableType === ScopeableType.ProductOffering &&
                  typeof s.scopeableId === 'number',
              )
              .map(s => s.scopeableId as number),
          );

          const toggleOffering = (offeringId: number, next: boolean): void => {
            if (next) {
              if (selectedIds.has(offeringId)) return;
              onChange([
                ...selectedScopes,
                {
                  scopeableType: ScopeableType.ProductOffering,
                  scopeableId: offeringId,
                },
              ]);
            } else {
              onChange(
                selectedScopes.filter(
                  s =>
                    !(
                      s.scopeableType === ScopeableType.ProductOffering &&
                      s.scopeableId === offeringId
                    ),
                ),
              );
            }
          };

          const toggleGroup = (
            group: OfferingGroup,
            selectAll: boolean,
          ): void => {
            const groupIds = new Set(group.offerings.map(o => o.id));
            const withoutGroup = selectedScopes.filter(
              s =>
                !(
                  s.scopeableType === ScopeableType.ProductOffering &&
                  typeof s.scopeableId === 'number' &&
                  groupIds.has(s.scopeableId)
                ),
            );
            if (selectAll) {
              const additions: ScopeItem[] = group.offerings.map(o => ({
                scopeableType: ScopeableType.ProductOffering,
                scopeableId: o.id,
              }));
              onChange([...withoutGroup, ...additions]);
            } else {
              onChange(withoutGroup);
            }
          };

          return (
            <HHStack spacing={0}>
              {groups.map(group => {
                const selectedInGroup = group.offerings.filter(o =>
                  selectedIds.has(o.id),
                ).length;
                const allSelected =
                  group.offerings.length > 0 &&
                  selectedInGroup === group.offerings.length;
                const someSelected = selectedInGroup > 0 && !allSelected;
                const groupKey = group.productId ?? UNGROUPED;
                const groupTestId = `product-offering-group-${group.productName.toLowerCase().replace(/\s+/g, '-')}`;

                return (
                  <HHAccordion
                    key={groupKey}
                    disableGutters
                    sx={(theme): object => ({
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                      backgroundColor: theme.palette.background.paper,
                    })}
                  >
                    <HHAccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        padding: 0,
                        margin: 0,
                        minHeight: 'unset',
                        '&.Mui-expanded': { minHeight: 'unset' },
                        '& .MuiAccordionSummary-content': {
                          margin: '4px 0',
                          '&.Mui-expanded': { margin: '4px 0' },
                        },
                      }}
                    >
                      <HHFormControlLabel
                        control={
                          <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={(_, checked): void =>
                              toggleGroup(group, checked)
                            }
                            onClick={(e): void => e.stopPropagation()}
                            size="small"
                            disabled={disabled}
                            data-testid={`${groupTestId}-master-checkbox`}
                          />
                        }
                        label={
                          <HHTypography hhVariant="body2">
                            {group.productName}
                          </HHTypography>
                        }
                        onClick={(e): void => e.stopPropagation()}
                        data-testid={groupTestId}
                      />
                    </HHAccordionSummary>
                    <HHAccordionDetails>
                      <HHStack
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: 0.5,
                          pl: 4,
                        }}
                      >
                        {group.offerings.map(offering => (
                          <HHFormControlLabel
                            key={offering.id}
                            control={
                              <Checkbox
                                checked={selectedIds.has(offering.id)}
                                onChange={(e): void =>
                                  toggleOffering(offering.id, e.target.checked)
                                }
                                size="small"
                                disabled={disabled}
                              />
                            }
                            label={
                              <HHTypography hhVariant="body2">
                                {offering.name}
                              </HHTypography>
                            }
                            data-testid={`product-offering-${offering.id}-checkbox`}
                          />
                        ))}
                      </HHStack>
                    </HHAccordionDetails>
                  </HHAccordion>
                );
              })}
            </HHStack>
          );
        }}
      />
    </HHStack>
  );
};
